import { Link } from "react-router-dom";
import MarketingNav from "@/components/app/MarketingNav";
import OtterIcon from "@/components/app/OtterIcon";
import { Button } from "@/components/ui/button";
import useScrollToHash from "@/hooks/use-scroll-to-hash";
import {
  Search, Globe2, LineChart, Link2, Target, Sparkles, ArrowRight, Check, Zap, Share2
} from "lucide-react";

const FEATURES = [
  { icon: Search, title: "Keyword Tracking", desc: "Track rankings, volume, difficulty and CPC for every keyword that matters." },
  { icon: Globe2, title: "On-page Audit", desc: "Crawl any URL and surface meta, heading, schema and performance issues in seconds." },
  { icon: Link2, title: "Backlink Intel", desc: "Inspect referring domains, anchor text and domain authority signals." },
  { icon: Target, title: "Competitor Lens", desc: "Benchmark your site against rivals on traffic, keywords and authority." },
  { icon: Sparkles, title: "AI Recommendations", desc: "Claude-powered action plans translate raw data into a prioritized roadmap." },
  { icon: Share2, title: "Social Post Writer", desc: "Turn a keyword, blog or URL into LinkedIn, X, Instagram, Facebook, Threads and Bluesky posts." },
];

const PLANS = [
  { name: "Free trial", price: "$0", period: "/ 7 days", features: ["1 project", "Full Pro features to evaluate", "No card required", "Then pick a paid plan"], cta: "Start free", primary: false },
  { name: "Starter", price: "$19", period: "/month", features: ["1 project (for SMBs)", "Simple Mode dashboard", "AI inline fixes & content drafts", "WordPress one-click publish", "Weekly digest emails"], cta: "Choose Starter", primary: false },
  { name: "Pro", price: "$39", period: "/month", features: ["5 projects", "Full Pro Dashboard", "AI Content Studio + section gen", "Content Gap (SERP analysis)", "Notion + WordPress publishing"], cta: "Try Pro", primary: true },
  { name: "Agency", price: "$99", period: "/month", features: ["25 projects", "Everything in Pro", "Priority generation queue", "Priority support", "Early access to new platforms"], cta: "Upgrade to Agency", primary: false },
];

export default function Landing() {
  useScrollToHash();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />

      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 border border-border bg-white px-3 py-1.5 text-xs tracking-wide" data-testid="hero-badge">
              <OtterIcon size={14} className="text-foreground" />
              <span className="font-medium">Meet Arlo · your SEO otter</span>
            </div>
            <h1 className="font-semibold tracking-tight mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
              Arlo audits while you{" "}
              <span className="bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">
                sip your coffee
              </span>
              .
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
              ArloSEO is the AI-powered SEO workspace that crawls your site, ranks your keywords,
              writes the content and even publishes to WordPress — so you can do, you know, literally anything else.
              <span className="block mt-2 text-sm italic">Otterly automated. (Sorry. We had to.)</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/quick-start">
                <Button size="lg" className="rounded-none bg-foreground hover:bg-foreground/90 px-7 h-12" data-testid="hero-cta-quick-start">
                  Quick start (90 seconds) <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="rounded-none border-foreground/40 hover:bg-foreground hover:text-background px-7 h-12" data-testid="hero-cta-register">
                  Start with full tools
                </Button>
              </Link>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">New?</span> Use Quick start — pick your business type, Arlo picks the keywords. No SEO knowledge required.
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs text-muted-foreground">
              <span>No credit card required</span>
              <span>·</span>
              <span>Add unlimited URLs in your trial</span>
              <span>·</span>
              <span>Cancel anytime</span>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="border border-border bg-white">
              <div className="border-b border-border px-4 py-2 flex items-center justify-between">
                <span className="font-mono-data text-xs">LIVE_SAMPLE.audit</span>
                <span className="font-mono-data text-xs text-primary">200 OK</span>
              </div>
              <div className="p-5 space-y-4 font-mono-data text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">SCORE</span><span className="text-2xl font-semibold font-display">82</span></div>
                <div className="h-px bg-border" />
                <div className="space-y-2">
                  {["Title length optimal","Meta description ok","H1 present","JSON-LD detected"].map(t=>(
                    <div key={t} className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-600"/><span>{t}</span></div>
                  ))}
                </div>
                <div className="h-px bg-border" />
                <div className="space-y-1.5">
                  <div className="flex justify-between"><span className="text-muted-foreground">WORDS</span><span>1,842</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">IMG_ALT_MISS</span><span className="text-amber-700">3/24</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">LCP</span><span>1.4s</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF STRIP */}
      <section className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-6 text-xs text-muted-foreground">
          <span className="font-mono-data uppercase tracking-widest">Powering search teams at</span>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-3 font-semibold text-foreground/50 text-base tracking-wide">
            <span>NORTHWIND</span><span>VECTRA</span><span>HALCYON</span><span>QUANTA</span><span>ATLAS/CO</span>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid md:grid-cols-12 gap-6 mb-12">
            <div className="md:col-span-5">
              <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">// features</p>
              <h2 className="font-semibold tracking-tight text-3xl lg:text-4xl mt-3 leading-tight">
                Every signal,<br/>one workspace.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-muted-foreground leading-relaxed">
                Stop bouncing between six tools. ArloSEO brings audits, keyword tracking,
                backlinks, competitors and AI strategy into a single, dense console designed
                for people who care about shipping.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 -mb-px -mr-px">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="border border-border -mr-px -mb-px p-8 bg-white tilt-hover"
                data-testid={`feature-card-${i}`}
              >
                <f.icon className="w-6 h-6" strokeWidth={1.5} />
                <h3 className="font-semibold text-lg mt-6 tracking-tight">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-b border-border bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="font-mono-data text-xs uppercase tracking-widest text-background/60">// pipeline</p>
          <h2 className="font-semibold tracking-tight text-3xl lg:text-4xl mt-3 max-w-3xl leading-tight">
            Add a URL. Get a roadmap. Ship the work.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { n: "01", t: "Connect a domain", d: "Add the website you want to grow. We index it on the spot — no scripts to install." },
              { n: "02", t: "Track what matters", d: "Pin keywords, competitors and crawl pages. Every metric updates in real time." },
              { n: "03", t: "Execute with AI", d: "Generate a prioritized action plan and content ideas tailored to your data." },
            ].map((s) => (
              <div key={s.n} className="border border-background/20 p-8">
                <span className="font-mono-data text-sm text-background/60">{s.n}</span>
                <h3 className="font-semibold text-xl mt-4 tracking-tight">{s.t}</h3>
                <p className="text-sm text-background/70 mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">// pricing</p>
            <h2 className="font-semibold tracking-tight text-3xl lg:text-4xl mt-3 leading-tight">
              Honest pricing. No keyword caps games.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {PLANS.map((p) => (
              <div key={p.name} className={`border p-7 tilt-hover ${p.primary ? "border-foreground bg-foreground text-background" : "border-border bg-white"}`}>
                <div className="flex items-baseline justify-between">
                  <h3 className="font-semibold text-xl tracking-tight">{p.name}</h3>
                  {p.primary && <span className="text-xs font-mono-data uppercase tracking-widest">popular</span>}
                </div>
                <div className="mt-6">
                  <span className="font-semibold text-5xl tracking-tight">{p.price}</span>
                  <span className={`text-sm ${p.primary ? "text-background/60" : "text-muted-foreground"}`}>{p.period}</span>
                </div>
                <ul className="mt-6 space-y-2 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/pricing" className="block mt-8">
                  <Button
                    className={`w-full rounded-none h-11 ${p.primary ? "bg-background text-foreground hover:bg-background/90" : "bg-foreground text-background hover:bg-foreground/90"}`}
                    data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                  >
                    {p.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-foreground grid place-items-center text-background">
              <OtterIcon size={16} />
            </div>
            <span className="font-semibold tracking-tight">ArloSEO</span>
            <span className="text-xs text-muted-foreground ml-2">© {new Date().getFullYear()} — built for technical operators.</span>
          </div>
          <nav className="flex items-center gap-5 text-xs text-muted-foreground">
            <Link to="/docs" className="hover:text-foreground" data-testid="footer-docs">Docs</Link>
            <Link to="/blog" className="hover:text-foreground" data-testid="footer-blog">Blog</Link>
            <Link to="/pricing" className="hover:text-foreground" data-testid="footer-pricing">Pricing</Link>
            <Link to="/status" className="hover:text-foreground" data-testid="footer-status">Status</Link>
          </nav>
          <div className="text-xs text-muted-foreground font-mono-data">
            STATUS · ALL SYSTEMS NOMINAL
          </div>
        </div>
      </footer>
    </div>
  );
}
